import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import {
  Stethoscope, CheckCircle, XCircle, Clock, Search, Filter
} from 'lucide-react';
import Pagination from '../../components/Pagination';

/**
 * DoctorManagement — Admin page to approve/verify/manage doctors.
 */
const DoctorManagement = () => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('PENDING');
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  const fetchDoctors = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/admin/doctors?status=${filter}`).catch(() => null);
      setDoctors(res?.data?.data || res?.data || []);
    } catch (err) {
      console.error('Failed to fetch doctors:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDoctors(); setPage(1); }, [filter]);

  const handleAction = async (doctorId, action) => {
    try {
      await api.patch(`/admin/doctors/${doctorId}/verification`, { status: action });
      toast.success(`Doctor ${action.toLowerCase()}`);
      fetchDoctors();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Action failed');
    }
  };

  return (
    <div className="space-y-6 py-6">
      <div>
        <h2 className="font-headline text-2xl font-extrabold text-slate-900 tracking-tight">Doctor Management</h2>
        <p className="text-slate-500 text-sm mt-1">Review, approve, and manage doctor accounts</p>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {['PENDING', 'APPROVED', 'REJECTED'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
              filter === status
                ? 'bg-primary text-white shadow-sm'
                : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            {status}
          </button>
        ))}
      </div>

      {/* Doctor List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="h-24 rounded-2xl animate-pulse bg-slate-100" />)}
        </div>
      ) : doctors.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-outline-variant/10">
          <Stethoscope className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <h3 className="font-bold text-lg text-slate-900 mb-1">No {filter.toLowerCase()} doctors</h3>
          <p className="text-sm text-slate-500">No doctors found with this status.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {doctors.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE).map((doc) => (
            <div key={doc._id} className="bg-white rounded-2xl p-5 border border-outline-variant/10 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center text-white font-extrabold text-base shrink-0">
                {(doc.name || 'Dr').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-sm text-slate-900 break-words">{doc.name}</h4>
                <p className="text-xs text-slate-500">{doc.email} · {doc.specialization || 'General'}</p>
              </div>
              {filter === 'PENDING' && (
                <div className="flex gap-2">
                  <button
                    onClick={() => handleAction(doc._id, 'APPROVED')}
                    className="flex items-center gap-1.5 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-xl text-xs font-bold hover:bg-emerald-100 transition-all"
                  >
                    <CheckCircle className="w-4 h-4" /> Approve
                  </button>
                  <button
                    onClick={() => handleAction(doc._id, 'REJECTED')}
                    className="flex items-center gap-1.5 px-4 py-2 bg-red-50 text-red-600 rounded-xl text-xs font-bold hover:bg-red-100 transition-all"
                  >
                    <XCircle className="w-4 h-4" /> Reject
                  </button>
                </div>
              )}
              {filter !== 'PENDING' && (
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  filter === 'APPROVED' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'
                }`}>
                  {filter}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      <Pagination page={page} totalPages={Math.ceil(doctors.length / ITEMS_PER_PAGE)} onPageChange={setPage} />
    </div>
  );
};

export default DoctorManagement;
